import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ImagePlus, ScanFace } from 'lucide-react';
import { LoadingOverlay } from './LoadingOverlay';

export const Stage = ({ image, isLoading, loadingMessage, onUploadClick, onCameraClick, hasPhoto, bottomOverlay }) => (
  <div className="relative w-full aspect-[3/4] max-h-[55vh] sm:max-h-[60vh] lg:max-h-[65vh] bg-gradient-to-b from-slate-50 to-slate-100 rounded-2xl overflow-hidden border border-slate-200">
    <AnimatePresence mode="wait">
      {image ? (
        <motion.img
          key={image}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          src={image}
          alt="Try-on result"
          className="w-full h-full object-contain"
        />
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="w-full h-full flex flex-col items-center justify-center p-6 sm:p-8"
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-sm flex items-center justify-center mb-3 sm:mb-4">
            <ScanFace className="w-8 h-8 sm:w-10 sm:h-10 text-slate-300" />
          </div>
          <p className="text-slate-500 text-center text-sm font-medium">Add your photo to start</p>
          <p className="text-slate-400 text-center text-xs mt-1">Take a selfie or upload from gallery</p>
        </motion.div>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {!hasPhoto && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="absolute bottom-6 sm:bottom-8 inset-x-0 flex justify-center gap-3 sm:gap-4"
        >
          <button
            onClick={(e) => { e.stopPropagation(); onCameraClick(); }}
            className="flex flex-col items-center gap-1 sm:gap-1.5 px-4 sm:px-5 py-2.5 sm:py-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-500/30 hover:bg-blue-700 hover:scale-105 transition-all"
            data-testid="camera-button"
          >
            <Camera className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-xs font-medium">Camera</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onUploadClick(); }}
            className="flex flex-col items-center gap-1 sm:gap-1.5 px-4 sm:px-5 py-2.5 sm:py-3 bg-white text-slate-700 rounded-2xl shadow-lg border border-slate-200 hover:bg-slate-50 hover:scale-105 transition-all"
            data-testid="upload-button"
          >
            <ImagePlus className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-xs font-medium">Upload</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {isLoading && <LoadingOverlay message={loadingMessage} />}
    </AnimatePresence>

    {/* Optional overlay anchored to the bottom of the stage (e.g. AI Color
        Change swatches). Rendered above the image but below the loading
        overlay, in the same rounded container so it visually belongs to
        the result. */}
    {bottomOverlay && (
      <div className="absolute inset-x-2 bottom-2 sm:inset-x-3 sm:bottom-3 z-20 pointer-events-auto">
        {bottomOverlay}
      </div>
    )}
  </div>
);
